import { PaletteAlias, setColorPalette } from '@neptune/colors-domain';
import { DBPersistedMyRunsConfig } from '@neptune/home-page-domain';
import { backendClient } from '@neptune/shared/core-apis-backend-domain';
import { DbPersistedThemeConfig, isThemeAlias, themeStore } from '@neptune/shared/theme-domain';

import { featureFlagStore } from './featureFlag-storage';
import { DbPersistedFeatureFlags } from './featureFlag-types';
import { DbPersistedPreferences, isLegendPosition, preferencesStore } from './preferences-storage';

type ReduxStore = {
  getState: () => any;
  dispatch: (action: any) => void;
};

type DbPersistedConfig = DbPersistedFeatureFlags &
  DbPersistedThemeConfig &
  DbPersistedPreferences &
  DBPersistedMyRunsConfig & {
    colorPalette: PaletteAlias;
  };

type DbPersistedConfigStore = {
  initialize(username: string | undefined, reduxStore: ReduxStore): Promise<void>;
  getItem<K extends keyof DbPersistedConfig>(key: K): DbPersistedConfig[K] | undefined;
  setItem<K extends keyof DbPersistedConfig>(key: K, value: DbPersistedConfig[K]): Promise<void>;
  isInitialized(): boolean;
};

/**
 * Creates a persistent config store that manages config state across API, local storage, and in-memory cache.
 * Prioritizes API as source of truth with local storage fallback.
 * Used for user-exposed feature flags (Experimental Features) and theme.
 */
export function createDbPersistedConfigStore(): DbPersistedConfigStore {
  const CONFIGURATION_KEY = 'neptune-configuration';

  let isInitialized = false;
  let configCache: Partial<DbPersistedConfig> = {};
  let currentUsername: string | undefined;

  /**
   * Initializes the store by loading configuration from API first, then local storage as fallback.
   */
  async function initialize(username: string | undefined, reduxStore: ReduxStore): Promise<void> {
    if (isInitialized) {
      return;
    }

    currentUsername = username;

    preferencesStore.initialize(reduxStore);

    const configFromApi = await loadFromApi();
    const featureFlagsFromLocalStorage = featureFlagStore.getDbPersistedFeatureFlags();
    const themeConfigFromLocalStorage = {
      selectedTheme: themeStore.getTheme(),
    };
    const preferencesConfigFromLocalStorage = {
      legendPosition: preferencesStore.getLegendPosition(),
    };
    const configFromLocalStorage = {
      ...featureFlagsFromLocalStorage,
      ...themeConfigFromLocalStorage,
      ...preferencesConfigFromLocalStorage,
    };

    if (configFromApi) {
      configCache = configFromApi;
      const featureFlagsToSet = Object.fromEntries(
        Object.entries(configFromApi).filter(([key]) =>
          featureFlagStore.isKeyInDbPersistedFeatureFlags(key),
        ),
      );
      featureFlagStore.setDbPersistedFeatureFlags(featureFlagsToSet);

      // If the config from the API is missing the theme or legend position,
      // update the cache with the local storage values and persist to the API.
      let shouldUpdateDb = false;

      if (configFromApi.colorPalette) {
        setColorPalette(configFromApi.colorPalette);
      }

      if (!configFromApi.selectedTheme) {
        configCache.selectedTheme = themeConfigFromLocalStorage.selectedTheme;
        shouldUpdateDb = true;
      }

      if (!configFromApi.legendPosition) {
        configCache.legendPosition = preferencesConfigFromLocalStorage.legendPosition;
        shouldUpdateDb = true;
      }

      if (shouldUpdateDb) {
        await persistToApi();
      }
    } else if (configFromLocalStorage && Object.keys(configFromLocalStorage).length > 0) {
      configCache = configFromLocalStorage;
      await persistToApi();
    } else {
      configCache = {};
    }

    isInitialized = true;
    featureFlagStore.setDbPersistedConfigStoreInitialized(true);
  }

  /**
   * Loads configs from the API.
   */
  async function loadFromApi(): Promise<Partial<DbPersistedConfig> | null> {
    if (!currentUsername) {
      return null;
    }

    try {
      const response = await backendClient.getScopedProperty({
        propertyType: 'USER',
        scope: currentUsername,
        key: CONFIGURATION_KEY,
      });

      return response.value.trim() ? JSON.parse(response.value.trim()) : null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load configuration from API', error);
      return null;
    }
  }

  /**
   * Gets a config value from the in-memory cache.
   */
  function getItem<K extends keyof DbPersistedConfig>(key: K): DbPersistedConfig[K] | undefined {
    if (!isInitialized) {
      throw new Error('PersistedConfigStore not initialized');
    }

    return configCache[key];
  }

  /**
   * Sets a config value and persists it to both local storage and API.
   */
  async function setItem<K extends keyof DbPersistedConfig>(
    key: K,
    value: DbPersistedConfig[K],
  ): Promise<void> {
    if (!isInitialized) {
      throw new Error('PersistedConfigStore not initialized');
    }

    const previousValue = configCache[key];

    configCache[key] = value;

    if (featureFlagStore.isKeyInDbPersistedFeatureFlags(key) && typeof value === 'boolean') {
      featureFlagStore.setDbPersistedFeatureFlag(key as keyof DbPersistedFeatureFlags, value);
    } else if (themeStore.isThemeConfigKeyValid(key) && isThemeAlias(value)) {
      themeStore.setTheme(value);
    } else if (preferencesStore.isLegendPositionConfigKeyValid(key) && isLegendPosition(value)) {
      preferencesStore.setLegendPosition(value);
    }

    try {
      await persistToApi(true);
    } catch (error) {
      configCache[key] = previousValue;

      if (
        featureFlagStore.isKeyInDbPersistedFeatureFlags(key) &&
        typeof previousValue === 'boolean'
      ) {
        featureFlagStore.setDbPersistedFeatureFlag(
          key as keyof DbPersistedFeatureFlags,
          previousValue,
        );
      } else if (themeStore.isThemeConfigKeyValid(key) && isThemeAlias(previousValue)) {
        themeStore.setTheme(previousValue);
      } else if (
        preferencesStore.isLegendPositionConfigKeyValid(key) &&
        isLegendPosition(previousValue)
      ) {
        preferencesStore.setLegendPosition(previousValue);
      }

      throw error;
    }
  }

  /**
   * Persists the entire configuration to the API.
   */
  async function persistToApi(rethrow = false): Promise<void> {
    if (!currentUsername) {
      return;
    }

    try {
      await backendClient.setScopedProperty({
        propertyType: 'USER',
        scope: currentUsername,
        propertyBody: {
          key: CONFIGURATION_KEY,
          value: JSON.stringify(configCache),
        },
      });
    } catch (error) {
      if (rethrow) {
        throw error;
      }
    }
  }

  function isInitializedCheck(): boolean {
    return isInitialized;
  }

  return {
    initialize,
    getItem,
    setItem,
    isInitialized: isInitializedCheck,
  };
}

export const dbPersistedConfigStore = createDbPersistedConfigStore();
